
import { api, HAS_CLOUD_SUPPORT } from "./api";
import { LeagueChallenge, LeagueRunSubmission, LeagueSeason, LeagueStanding } from "../league/types";
import { syncQueueService } from "./syncQueueService";

// Initialisation de l'auto-sync
syncQueueService.initAutoSync();

// --- LOCAL CACHE KEYS ---
const CACHE_KEYS = {
    SEASON: 'gsc_league_season',
    CHALLENGES: 'gsc_league_challenges',
    STANDINGS: 'gsc_league_standings'
};

// --- CACHE HELPERS ---
const cache = {
    get: <T>(key: string): T | null => {
        try {
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch { return null; }
    },
    set: (key: string, data: any) => {
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch {}
    }
};

// --- UTILS ---
const getWeekNumber = (d: Date) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// --- ROBUST HYBRID BACKEND ---
// Ce backend gère automatiquement le fallback cache/queue sans lever d'exception bloquante.

const RobustBackend = {
    async getSeason(): Promise<LeagueSeason> {
        // 1. Try Network
        if (HAS_CLOUD_SUPPORT && navigator.onLine) {
            const res = await api.get<LeagueSeason>('/league/season/current');
            if (res.success && res.data) {
                cache.set(CACHE_KEYS.SEASON, res.data);
                return res.data;
            }
        }
        
        // 2. Try Cache
        const cached = cache.get<LeagueSeason>(CACHE_KEYS.SEASON);
        if (cached) return cached;

        // 3. Fallback Generate (Mode Déconnecté total ou premier load sans réseau)
        const now = new Date();
        const seasonIndex = Math.ceil((now.getMonth() + 1) / 3);
        const year = now.getFullYear();
        return {
            id: `season_${year}_Q${seasonIndex}_OFFLINE`,
            name: `Saison ${year} - Q${seasonIndex} (Mode Hors-Ligne)`,
            startAt: new Date(year, (seasonIndex - 1) * 3, 1).toISOString(),
            endAt: new Date(year, seasonIndex * 3, 0).toISOString(),
        };
    },

    async getWeeklyChallenges(seasonId: string): Promise<LeagueChallenge[]> {
        if (HAS_CLOUD_SUPPORT && navigator.onLine) {
            const res = await api.get<LeagueChallenge[]>(`/league/season/${seasonId}/challenges`);
            if (res.success && res.data) {
                cache.set(CACHE_KEYS.CHALLENGES, res.data);
                return res.data;
            }
        }

        const cached = cache.get<LeagueChallenge[]>(CACHE_KEYS.CHALLENGES);
        if (cached) return cached;

        // Fallback Local Challenges
        const weekNum = getWeekNumber(new Date());
        return [
            { id: `local_w${weekNum}_c1`, seasonId, weekIndex: weekNum, title: "Knocking on Heaven's Door", bpm: 65, grid: "G D | Am | G D | C", createdAt: new Date().toISOString() },
            { id: `local_w${weekNum}_c2`, seasonId, weekIndex: weekNum, title: "Zombie", bpm: 80, grid: "Em | C | G | D", createdAt: new Date().toISOString() },
            { id: `local_w${weekNum}_c3`, seasonId, weekIndex: weekNum, title: "Offline Drill", bpm: 90, grid: "E | A | B | E", createdAt: new Date().toISOString() }
        ];
    },

    async getStandings(seasonId: string): Promise<LeagueStanding[]> {
        if (HAS_CLOUD_SUPPORT && navigator.onLine) {
            const res = await api.get<LeagueStanding[]>(`/league/season/${seasonId}/standings`);
            if (res.success && res.data) {
                cache.set(CACHE_KEYS.STANDINGS, res.data);
                return res.data;
            }
        }

        const cached = cache.get<LeagueStanding[]>(CACHE_KEYS.STANDINGS);
        return cached || [
            { userId: "offline_bot", userName: "Offline_Ghost", division: "GOLD", lp: 1500, rank: 1, totalRuns: 10, avgScore: 85, bestScore: 90 }
        ];
    },

    async submitRun(run: LeagueRunSubmission): Promise<{ addedLP: number; standing: LeagueStanding }> {
        // 1. Try Direct Network Submission
        if (HAS_CLOUD_SUPPORT && navigator.onLine) {
            const res = await api.post<{ addedLP: number; standing: LeagueStanding }>('/league/run', run);
            if (res.success && res.data) return res.data;
            
            // Si erreur réseau spécifique, on passe à la suite (Enqueue)
            if (res.error !== "NETWORK_ERROR" && res.error !== "Failed to fetch") {
                throw new Error(res.error || "Submission Rejected");
            }
        }

        // 2. Offline Queue Strategy
        syncQueueService.enqueue(run);

        // 3. Optimistic Response (Feedback UI immédiat)
        // On renvoie un standing fictif basé sur le cache ou des valeurs par défaut
        const cachedStandings = cache.get<LeagueStanding[]>(CACHE_KEYS.STANDINGS) || [];
        const myStanding = cachedStandings.find(s => s.userId === run.userId) || {
            userId: run.userId,
            userName: run.userName,
            division: "BRONZE",
            lp: 0,
            rank: 999,
            totalRuns: 0,
            avgScore: 0,
            bestScore: 0
        };

        return {
            addedLP: 0, // Sera calculé serveur lors de la sync
            standing: {
                ...myStanding,
                totalRuns: myStanding.totalRuns + 1, // Optimistic update
                lp: myStanding.lp // On ne devine pas les LP
            }
        };
    }
};

export const leagueService = {
  backend: RobustBackend,
};
